import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { type ObserveSession } from "@/hooks/useObserveSession";

/**
 * What Aiva is doing to the screen, as opposed to merely watching it.
 *
 * `failed` is distinct from `idle` because the hire has not acknowledged it yet: the banner stays on
 * screen until they do, which is what turns an acknowledgement into a state change.
 */
export type AivaAutomationPhase = "idle" | "driving" | "failed";

export interface AivaAutomation {
  readonly phase: AivaAutomationPhase;
  /** How many runs have failed this session, which decides whether Aiva offers a manual fallback. */
  readonly failureCount: number;
  /** Ends the run, or acknowledges a failed one. Absent while nothing is running. */
  readonly onStop?: () => void;
  /** Sends the hire to manual instructions instead of retrying. Absent below the retry threshold. */
  readonly onManualFallback?: () => void;
}

export interface AivaActivity {
  /**
   * Whether a journey recording has been started and not ended. This is what the widget asked the
   * host for, not proof the host is capturing.
   */
  readonly isRecording: boolean;
  /** Whether the hire paused capture. Their consent stands. No banner surfaces this today. */
  readonly isPaused: boolean;
  /** Whether resuming would actually record. No banner surfaces this today either. */
  readonly canRecord: boolean;
  /** Pauses capture. Optional for a consumer with no capture surface; the wizard always supplies it. */
  readonly onPause?: () => void;
  /** Resumes it, for the same consumers. */
  readonly onResume?: () => void;
  readonly automation: AivaAutomation;
}

const IDLE: AivaAutomation = { phase: "idle", failureCount: 0 };

const AivaActivityContext = createContext<AivaActivity | undefined>(undefined);

type PublishAutomation = (automation: AivaAutomation) => void;

const AivaAutomationPublisherContext = createContext<
  PublishAutomation | undefined
>(undefined);

/**
 * Holds what Aiva is doing, so one banner above the wizard can report it.
 *
 * Split from the collapse context on purpose: this is a machine fact that changes as the agent runs,
 * where collapsing is a choice the hire made once.
 *
 * @param observe The observe loop's state and pause switch, from `useObserveSession`.
 */
export const AivaActivityProvider: React.FC<{
  readonly observe: ObserveSession;
  readonly children: ReactNode;
}> = ({ observe, children }) => {
  const [automation, setAutomation] = useState<AivaAutomation>(IDLE);

  const publish = useCallback<PublishAutomation>((next) => {
    setAutomation((previous) =>
      previous.phase === next.phase &&
      previous.failureCount === next.failureCount &&
      previous.onStop === next.onStop &&
      previous.onManualFallback === next.onManualFallback
        ? previous
        : next
    );
  }, []);

  const activity = useMemo(
    () => ({
      automation,
      canRecord: observe.canRecord,
      isPaused: observe.isPaused,
      isRecording: observe.isRecording,
      onPause: observe.pause,
      onResume: observe.resume,
    }),
    [automation, observe]
  );

  return (
    <AivaAutomationPublisherContext.Provider value={publish}>
      <AivaActivityContext.Provider value={activity}>
        {children}
      </AivaActivityContext.Provider>
    </AivaAutomationPublisherContext.Provider>
  );
};

/**
 * What Aiva is doing, for whatever renders the banner.
 *
 * Throws without a provider rather than falling back, unlike the collapse hook. A banner that
 * silently read "idle" forever would look exactly like a working one that Aiva never engaged.
 */
export const useAivaActivity = (): AivaActivity => {
  const provided = useContext(AivaActivityContext);
  if (provided === undefined) {
    throw new Error("useAivaActivity requires an AivaActivityProvider");
  }
  return provided;
};

/**
 * Reports what Aiva is doing, for a step that runs it.
 *
 * A no-op without a provider, because a step is renderable on its own and must not depend on the
 * page having mounted a banner. The reader is the half that insists on the provider.
 */
export const usePublishAivaAutomation = (): PublishAutomation => {
  const publish = useContext(AivaAutomationPublisherContext);
  const ignore = useCallback<PublishAutomation>(() => undefined, []);
  return publish ?? ignore;
};

/** The phase to publish when nothing is running, so callers do not each spell out the idle shape. */
export const IDLE_AUTOMATION = IDLE;
